import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { ApiRequestError } from './api-client';

/**
 * One QueryClient for the app.
 *
 * Failed *mutations* surface a toast automatically — a user who clicks Save must
 * always learn whether it worked. Failed *queries* do not, because screens render
 * their own ErrorState with a retry affordance in place of the content.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: (failureCount, error) => {
        // Auth, permission and validation failures will not fix themselves.
        if (error instanceof ApiRequestError && error.status < 500) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: false,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      // Only complain about background refetch failures once data is on screen.
      if (query.state.data !== undefined && error instanceof ApiRequestError) {
        toast.error('Could not refresh this data', { description: error.message });
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      if (!(error instanceof ApiRequestError)) {
        toast.error('Something went wrong', { description: 'Please try again.' });
        return;
      }
      // Field-level validation is rendered inline on the form, not as a toast.
      if (error.code === 'VALIDATION_ERROR' && error.details?.length) return;
      if (error.isAuthError) return;

      toast.error(error.code === 'FORBIDDEN' ? 'Not permitted' : 'Could not save your changes', {
        description: error.message,
      });
    },
  }),
});

/**
 * Query key factory.
 *
 * Centralising keys keeps invalidation honest: a mutation invalidates
 * `queryKeys.users.all` and every list and detail under it refreshes, with no
 * string literals scattered across features. Add a block per module.
 */
export const queryKeys = {
  auth: {
    me: ['auth', 'me'] as const,
    sessions: ['auth', 'sessions'] as const,
  },
  dashboard: {
    all: ['dashboard'] as const,
    summary: ['dashboard', 'summary'] as const,
  },
  users: {
    all: ['users'] as const,
    list: (params: object) => ['users', 'list', params] as const,
    detail: (id: string) => ['users', 'detail', id] as const,
    assignable: (search?: string) => ['users', 'assignable', search ?? ''] as const,
    branches: ['users', 'branches'] as const,
  },
  roles: {
    all: ['roles'] as const,
    list: (params: object) => ['roles', 'list', params] as const,
    detail: (id: string) => ['roles', 'detail', id] as const,
    permissionCatalog: ['roles', 'permission-catalog'] as const,
  },
  auditLogs: {
    all: ['audit-logs'] as const,
    list: (params: object) => ['audit-logs', 'list', params] as const,
    entityTypes: ['audit-logs', 'entity-types'] as const,
  },
  customers: {
    all: ['customers'] as const,
    list: (params: object) => ['customers', 'list', params] as const,
    detail: (id: string) => ['customers', 'detail', id] as const,
    options: (search?: string) => ['customers', 'options', search ?? ''] as const,
  },
  leads: {
    all: ['leads'] as const,
    list: (params: object) => ['leads', 'list', params] as const,
    detail: (id: string) => ['leads', 'detail', id] as const,
    activities: (id: string) => ['leads', 'activities', id] as const,
    followUps: (params: object) => ['leads', 'follow-ups', params] as const,
  },
  leadSources: {
    all: ['lead-sources'] as const,
    list: (params: object) => ['lead-sources', 'list', params] as const,
    options: ['lead-sources', 'options'] as const,
  },
  appointments: {
    all: ['appointments'] as const,
    list: (params: object) => ['appointments', 'list', params] as const,
    detail: (id: string) => ['appointments', 'detail', id] as const,
  },
  products: {
    all: ['products'] as const,
    list: (params: object) => ['products', 'list', params] as const,
    detail: (id: string) => ['products', 'detail', id] as const,
    options: (search?: string) => ['products', 'options', search ?? ''] as const,
    images: (id: string) => ['products', 'images', id] as const,
  },
  productItems: {
    all: ['product-items'] as const,
    list: (params: object) => ['product-items', 'list', params] as const,
    detail: (id: string) => ['product-items', 'detail', id] as const,
    history: (id: string) => ['product-items', 'history', id] as const,
    timeline: (id: string) => ['product-items', 'timeline', id] as const,
    options: (search?: string) => ['product-items', 'options', search ?? ''] as const,
  },
  productCategories: {
    all: ['product-categories'] as const,
    list: (params: object) => ['product-categories', 'list', params] as const,
    options: (search?: string) => ['product-categories', 'options', search ?? ''] as const,
  },
  inventoryLocations: {
    all: ['inventory-locations'] as const,
    list: (params: object) => ['inventory-locations', 'list', params] as const,
    options: (search?: string) => ['inventory-locations', 'options', search ?? ''] as const,
  },
  quotations: {
    all: ['quotations'] as const,
    list: (params: object) => ['quotations', 'list', params] as const,
    detail: (id: string) => ['quotations', 'detail', id] as const,
    print: (id: string) => ['quotations', 'print', id] as const,
  },
  sales: {
    all: ['sales'] as const,
    list: (params: object) => ['sales', 'list', params] as const,
    detail: (id: string) => ['sales', 'detail', id] as const,
    invoice: (id: string) => ['sales', 'invoice', id] as const,
    summary: ['sales', 'summary'] as const,
    report: ['sales', 'report'] as const,
  },
  payments: {
    all: ['payments'] as const,
    list: (params: object) => ['payments', 'list', params] as const,
    detail: (id: string) => ['payments', 'detail', id] as const,
    receipt: (id: string) => ['payments', 'receipt', id] as const,
    collection: ['payments', 'collection'] as const,
    receivables: (params: object) => ['payments', 'receivables', params] as const,
    customerSummary: (id: string) => ['payments', 'customer-summary', id] as const,
  },
  rentalBookings: {
    all: ['rental-bookings'] as const,
    list: (params: object) => ['rental-bookings', 'list', params] as const,
    detail: (id: string) => ['rental-bookings', 'detail', id] as const,
    /**
     * Availability is keyed by the whole query, dates included, so changing the
     * date range is a different key and cannot serve a stale answer for the old
     * one — the single most important thing to get right on the booking form.
     */
    availability: (params: object) => ['rental-bookings', 'availability', params] as const,
  },
  rentalAgreements: {
    all: ['rental-agreements'] as const,
    list: (params: object) => ['rental-agreements', 'list', params] as const,
    detail: (id: string) => ['rental-agreements', 'detail', id] as const,
    print: (id: string) => ['rental-agreements', 'print', id] as const,
  },
  rentalDispatches: {
    all: ['rental-dispatches'] as const,
    list: (params: object) => ['rental-dispatches', 'list', params] as const,
    detail: (id: string) => ['rental-dispatches', 'detail', id] as const,
    print: (id: string) => ['rental-dispatches', 'print', id] as const,
  },
  rentalReturns: {
    all: ['rental-returns'] as const,
    list: (params: object) => ['rental-returns', 'list', params] as const,
    detail: (id: string) => ['rental-returns', 'detail', id] as const,
    print: (id: string) => ['rental-returns', 'print', id] as const,
    /**
     * The settlement is a separate key from the detail because it is projected
     * while the return is a draft: it changes every time an inspection amount
     * changes, and must not be served from a cached detail response.
     */
    settlement: (id: string) => ['rental-returns', 'settlement', id] as const,
    /** Agreement-scoped: the deposit ledger and the pieces still to come back. */
    deposits: (agreementId: string) => ['rental-returns', 'deposits', agreementId] as const,
    returnable: (agreementId: string) => ['rental-returns', 'returnable', agreementId] as const,
  },
  rentalReports: {
    all: ['rental-reports'] as const,
    /**
     * The calendar is keyed by its whole query, month included, so moving to
     * the next month is a different key and can never be served a stale answer
     * for the previous one.
     */
    calendar: (params: object) => ['rental-reports', 'calendar', params] as const,
    dashboard: ['rental-reports', 'dashboard'] as const,
    register: (params: object) => ['rental-reports', 'register', params] as const,
    active: (params: object) => ['rental-reports', 'active', params] as const,
    overdue: (params: object) => ['rental-reports', 'overdue', params] as const,
    revenue: (params: object) => ['rental-reports', 'revenue', params] as const,
    deposits: (params: object) => ['rental-reports', 'deposits', params] as const,
    utilization: (params: object) => ['rental-reports', 'utilization', params] as const,
    customerSummary: (customerId: string) =>
      ['rental-reports', 'customer-summary', customerId] as const,
  },
  /**
   * Customer portal (Phase 6).
   *
   * A separate namespace from the internal keys, so a portal screen can never be
   * served a cached internal response and `queryClient.clear()` on portal
   * sign-in has nothing of the ERP's to leave behind.
   */
  portal: {
    all: ['portal'] as const,
    me: ['portal', 'me'] as const,
    dashboard: ['portal', 'dashboard'] as const,
    profile: ['portal', 'profile'] as const,
    outstanding: ['portal', 'outstanding'] as const,
    quotations: (params: object) => ['portal', 'quotations', params] as const,
    quotation: (id: string) => ['portal', 'quotation', id] as const,
    quotationDocument: (id: string) => ['portal', 'quotation-document', id] as const,
    sales: (params: object) => ['portal', 'sales', params] as const,
    sale: (id: string) => ['portal', 'sale', id] as const,
    invoice: (id: string) => ['portal', 'invoice', id] as const,
    payments: (params: object) => ['portal', 'payments', params] as const,
    receipt: (id: string) => ['portal', 'receipt', id] as const,
    rentals: (params: object) => ['portal', 'rentals', params] as const,
    rental: (id: string) => ['portal', 'rental', id] as const,
    buybacks: (params: object) => ['portal', 'buybacks', params] as const,
    buyback: (id: string) => ['portal', 'buyback', id] as const,
    notifications: (params: object) => ['portal', 'notifications', params] as const,
    notification: (id: string) => ['portal', 'notification', id] as const,
    notificationsUnread: ['portal', 'notifications-unread'] as const,
    preferences: ['portal', 'preferences'] as const,
  },
  /**
   * Notifications (Phase 7).
   *
   * `unread` is its own key rather than being derived from a list, because the
   * bell badge is refetched far more often than any list and must not drag a
   * page of rows along with it.
   */
  notifications: {
    all: ['notifications'] as const,
    list: (params: object) => ['notifications', 'list', params] as const,
    detail: (id: string) => ['notifications', 'detail', id] as const,
    unread: ['notifications', 'unread'] as const,
    summary: (params: object) => ['notifications', 'summary', params] as const,
    failures: (params: object) => ['notifications', 'failures', params] as const,
    templates: (includeSystem: boolean) =>
      ['notifications', 'templates', includeSystem] as const,
    templateVariables: ['notifications', 'template-variables'] as const,
    customerCommunication: (customerId: string, params: object) =>
      ['notifications', 'customer-communication', customerId, params] as const,
    customerPreferences: (customerId: string) =>
      ['notifications', 'customer-preferences', customerId] as const,
  },
  buybacks: {
    all: ['buybacks'] as const,
    list: (params: object) => ['buybacks', 'list', params] as const,
    detail: (id: string) => ['buybacks', 'detail', id] as const,
    print: (id: string) => ['buybacks', 'print', id] as const,
    /**
     * Keyed by customer *and* search term: the eligible set is different for
     * every customer, and serving one customer's owned pieces from another's
     * cached answer would offer a seller a garment the server will refuse.
     */
    eligibleItems: (customerId: string, search?: string) =>
      ['buybacks', 'eligible-items', customerId, search ?? ''] as const,
    summaryReport: (params: object) => ['buybacks', 'reports', 'summary', params] as const,
    payablesReport: (params: object) => ['buybacks', 'reports', 'payables', params] as const,
  },
  /**
   * Accounting (Phase 8).
   *
   * The statements are keyed by their window, because "the trial balance" is
   * meaningless without one — two windows are two different documents, and
   * serving one from the other's cache would show an accountant last quarter's
   * figures under this quarter's heading.
   */
  accounting: {
    all: ['accounting'] as const,
    summary: ['accounting', 'summary'] as const,
    integrity: ['accounting', 'integrity'] as const,
    accounts: (params: object) => ['accounting', 'accounts', params] as const,
    account: (id: string) => ['accounting', 'account', id] as const,
    periods: (params: object) => ['accounting', 'periods', params] as const,
    journals: (params: object) => ['accounting', 'journals', params] as const,
    journal: (id: string) => ['accounting', 'journal', id] as const,
    ledger: (params: object) => ['accounting', 'ledger', params] as const,
    trialBalance: (params: object) => ['accounting', 'trial-balance', params] as const,
    profitAndLoss: (params: object) => ['accounting', 'profit-loss', params] as const,
    balanceSheet: (params: object) => ['accounting', 'balance-sheet', params] as const,
    receivables: (params: object) => ['accounting', 'receivables', params] as const,
    journalPrint: (id: string) => ['accounting', 'journal-print', id] as const,
    trialBalancePrint: (params: object) => ['accounting', 'trial-balance-print', params] as const,
    ledgerPrint: (params: object) => ['accounting', 'ledger-print', params] as const,
  },
  inventoryValuation: {
    all: ['inventory-valuation'] as const,
    report: (params: object) => ['inventory-valuation', 'report', params] as const,
    item: (id: string) => ['inventory-valuation', 'item', id] as const,
    integrity: ['inventory-valuation', 'integrity'] as const,
  },
  /**
   * Vendors, purchases and payables (Phase 11).
   *
   * Three roots rather than one, because posting a purchase changes all three —
   * the document, the supplier's position and the payables total — and three
   * roots let the mutation invalidate exactly what moved. They are also three
   * permission namespaces, so a user may legitimately hold a cache for one and
   * never be allowed to fill another.
   */
  vendors: {
    all: ['vendors'] as const,
    list: (params: object) => ['vendors', 'list', params] as const,
    options: (search: string) => ['vendors', 'options', search] as const,
    detail: (id: string) => ['vendors', 'detail', id] as const,
    statement: (id: string, params: object) => ['vendors', 'statement', id, params] as const,
  },
  purchases: {
    all: ['purchases'] as const,
    list: (params: object) => ['purchases', 'list', params] as const,
    detail: (id: string) => ['purchases', 'detail', id] as const,
    print: (id: string) => ['purchases', 'print', id] as const,
  },
  /** Phase 12. A completed return changes a purchase, a vendor and the payables too. */
  purchaseReturns: {
    all: ['purchase-returns'] as const,
    list: (params: object) => ['purchase-returns', 'list', params] as const,
    detail: (id: string) => ['purchase-returns', 'detail', id] as const,
    print: (id: string) => ['purchase-returns', 'print', id] as const,
    preview: (purchaseId: string, lineIds: string[]) =>
      ['purchase-returns', 'preview', purchaseId, lineIds] as const,
  },
  integrations: {
    all: ['integrations'] as const,
    events: (id: string, params: object) => ['integrations', 'events', id, params] as const,
  },
  inbox: {
    all: ['inbox'] as const,
    list: (params: object) => ['inbox', 'list', params] as const,
    detail: (id: string) => ['inbox', 'detail', id] as const,
    counts: ['inbox', 'counts'] as const,
  },
  companySettings: {
    all: ['company-settings'] as const,
  },
  payables: {
    all: ['payables'] as const,
    summary: (params: object) => ['payables', 'summary', params] as const,
    aging: (params: object) => ['payables', 'aging', params] as const,
    dashboard: ['payables', 'dashboard'] as const,
    payments: (params: object) => ['payables', 'payments', params] as const,
    payment: (id: string) => ['payables', 'payment', id] as const,
    paymentPrint: (id: string) => ['payables', 'payment-print', id] as const,
    credits: (params: object) => ['payables', 'credits', params] as const,
  },
} as const;
